import { api } from './client';

export interface Party {
  id: number;
  code: string;
  name: string;
  alias: string | null;
  isCustomer: boolean;
  isSupplier: boolean;
  isContractor: boolean;
  isIncomeExpense: boolean;
  ledger: string;
  openingBalance: string;
  drCr: 'Dr' | 'Cr';
  gstin: string | null;
  pan: string | null;
  contactPerson: string | null;
  mobile: string | null;
  whatsapp: string | null;
  email: string | null;
  telephone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateCode: string | null;
  state: { name: string } | null;
  pin: string | null;
  country: string | null;
  customerType: 'Wholesaler' | 'Distributor' | 'Retailer' | 'MBO & SiS' | 'Corporate & Projects' | 'Architect' | 'Customization' | 'Lead' | null;
  discount: string | null;
  specialDiscount: string | null;
  friendsFamilyDiscount: string | null;
  region: string | null;
  sisGroup: string | null;
  creditLimit: string | null;
  paymentTermDays: number | null;
  outstanding: string;
  transport: string | null;
  courier: string | null;
  bankName: string | null;
  bankAccount: string | null;
  ifsc: string | null;
  reference: string | null;
}

export interface PartyListResult {
  data: Party[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PartyFilter {
  type?: 'customer' | 'vendor' | 'all' | 'lead';
  includeLeads?: boolean;
  /** Specific party type (a customerType, or 'Vendor'). Narrows within the tab. */
  customerType?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export async function listParties(filter: PartyFilter = {}): Promise<PartyListResult> {
  const r = await api.get<PartyListResult>('/parties', { params: filter });
  return r.data;
}

export async function getParty(id: number): Promise<Party> {
  const r = await api.get<Party>(`/parties/${id}`);
  return r.data;
}

export async function createParty(payload: Partial<Party>): Promise<Party> {
  const r = await api.post<Party>('/parties', payload);
  return r.data;
}

export async function updateParty(id: number, payload: Partial<Party>): Promise<Party> {
  const r = await api.patch<Party>(`/parties/${id}`, payload);
  return r.data;
}

export async function deleteParty(id: number): Promise<void> {
  await api.delete(`/parties/${id}`);
}

export async function deleteAllParties(): Promise<{ deleted: number }> {
  const r = await api.delete<{ deleted: number }>('/parties/all');
  return r.data;
}

export async function restoreCustomers(): Promise<{ restored: number }> {
  const r = await api.post<{ restored: number }>('/parties/restore-customers');
  return r.data;
}

/** Distinct SIS group / company names already in use — for the Party-form autocomplete. */
export async function fetchPartySisGroups(): Promise<string[]> {
  const r = await api.get<string[]>('/parties/sis-groups');
  return r.data;
}

export function partiesExportPath(filter: PartyFilter = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '')) as Record<string, string>
  ).toString();
  return `/parties/export.xlsx${search ? `?${search}` : ''}`;
}

export function partiesImportTemplatePath(): string {
  return '/parties/import-template.xlsx';
}

export async function importParties(file: File): Promise<import('./items').ImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post<import('./items').ImportResult>('/parties/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Bulk imports can run past the default 30s (large files, slow DB).
    // Allow up to 5 minutes so a 500-row upload doesn't fail on the wire
    // while the server is still processing.
    timeout: 5 * 60 * 1000,
  });
  return r.data;
}
